import downloadSvg from "assets/img/download.svg";
import loadingSvg from "assets/img/loading.svg";
import Button from "components/Button";
import ButtonWithConfirmation, { DialogIcon } from "components/ButtonWithConfirmation";
import { Card, CardList } from "components/Card";
import Dropdown from "components/Dropdown";
import FileStatusFilter from "components/FileStatusFilter";
import FileTypeFilter, { filterFilesByType } from "components/FileTypeFilter";
import Form from "components/Form";
import Icon from "components/Icon";
import Page from "components/Page";
import { Pill, PillType, getPillProps } from "components/Pill";
import Table from "components/Table";
import { Tooltip } from "components/Tooltip";
import config from "config";
import { RouteProps } from "containers/App";
import ProjectFilter, { FilterOptions } from "containers/ProjectFilter";
import Constants from "helpers/constants";
import { getItem } from "helpers/storage";
import { File } from "interfaces/file";
import { FetchAllStoresOrFilesQueryOptions } from "interfaces/filterQuery";
import { ProjectType } from "interfaces/project";
import { ReactSelectObject } from "interfaces/select";
import moment from "moment";
import React from "react";
import { ConnectedProps, connect } from "react-redux";
import { State } from "state";
import {
  DownloadSearchFields,
  EventTypes,
  FileStatus,
  FileType,
  createAnalyticsEvent,
} from "state/analytics";
import { CHAIN_ROLE } from "state/auth";
import { fetchAllFiles, initAllFiles } from "state/files";
import { fetchStoresByProjectId } from "state/stores";
import { sortStoreNumbersMixed } from "utils/sorters";
import { resolveStoreIdsForProject } from "utils/storeIds";
import { fetchZipForFilesDownload } from "../state/batch_download";

interface DownloadsState {
  tableData: File[];
  currentId: number;
  currentLabel: string;
  currentType: string;
  selectedFileTypes: ReactSelectObject[];
  selectedFileStatus: ReactSelectObject[];
  selectedAttachment: ReactSelectObject;
  query: FetchAllStoresOrFilesQueryOptions;
  fields: Set<string>;
  downloadMessage?: string;
  disableRepeatDownload?: boolean;
  hasProjectParam: boolean;
  currentProjectName: string;
  currentChainName: string;
}

type DownloadsProps = PropsFromRedux & RouteProps;

const fileTypeOptions = [
  { label: "PDF", value: "pdf", "data-qa": "pdf" },
  { label: "PSA", value: "psa", "data-qa": "psa" },
];

const fileStatusOptions = [
  { label: "Original", value: "Original", "data-qa": "Original" },
  { label: "Final", value: "Final", "data-qa": "Final" },
];

const STORE_ATTACHMENTS = "Store Attachments";
const PROJ_ATTACHMENTS = "Project Attachments";
const ALL_ATTACHMENTS = "All Attachments";

const attachmentOptions = [
  { label: ALL_ATTACHMENTS, value: "All" },
  { label: STORE_ATTACHMENTS, value: "Store", "data-qa": "store-attachments" },
  {
    label: PROJ_ATTACHMENTS,
    value: "Project",
    "data-qa": "project-attachments",
  },
];

const styles = {

  button: (bg: string, color: string, border: string = "none") => ({
    padding: "5px 15px",
    border,
    borderRadius: 4,
    backgroundColor: bg,
    color,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600
  }),
};

const statusArray = [
  "Store Status",
  // Constants.RESET_NOT_SCHEDULED,
  Constants.RESET_SCHEDULED,
  Constants.RESET_COMPLETE,
  Constants.NEEDS_VALIDATION,
  Constants.FILES_AVAILABLE,
  // Constants.VALIDATION_PENDING,
  // Constants.FEEDBACK_UNDER_REVIEW,
  // Constants.NEEDS_FEEDBACK_REVIEW,
  // Constants.AWAITING_FINAL_UPLOAD,
  Constants.VALIDATION_COMPLETE,
];

const userDropdownOptions = statusArray.map((arrVal, i) => ({
  label: arrVal,
  value: i === 0 ? "" : arrVal,
  "data-qa": arrVal,
}));

const defaultQuery: FetchAllStoresOrFilesQueryOptions = {
  storeName: "",
  storeNumber: "",
  status:"",
  address: "",
  city: "",
  state: "",
  zip: "",
  abWholesaler: "",
  millerWholesaler: "",
  coorsWholesaler: "",
  thirdPartyWholesaler: "",
  advertisingZone: "",
  cluster: "",
};

const defaultState: DownloadsState = {
  tableData: [],
  currentId: -999,
  currentLabel: "",
  currentType: "",
  selectedFileTypes: [],
  selectedFileStatus: [],
  selectedAttachment: attachmentOptions[0],
  query: defaultQuery,
  fields: new Set(),
  hasProjectParam: false,
  currentProjectName: "",
  currentChainName: ""
};

let showStoreColumns = true;

class Downloads extends React.Component<DownloadsProps, DownloadsState> {
  public state = defaultState;

  private resolveStoreIds = async (projectIdNum: number) => {
    return resolveStoreIdsForProject({
      projectId: projectIdNum,
      storeIdsInState: this.state.query.storeIds,
      storesInContext: this.props.stores || [],
      fetchStoresByProjectId: this.props.fetchStoresByProjectId as any,
    });
  };

  private buildTableData = (files: File[]) => {
    const { selectedAttachment, selectedFileStatus, selectedFileTypes } = this.state;

    showStoreColumns =
      selectedAttachment.label === STORE_ATTACHMENTS ||
      selectedAttachment.label === ALL_ATTACHMENTS;

    return filterFilesByType(
      selectedFileTypes,
      files.filter(
        (file: File) =>
          (file.maptype?.toUpperCase() === selectedAttachment.value.toUpperCase() ||
            selectedAttachment.label === ALL_ATTACHMENTS) &&
          (selectedFileStatus.length === 0 ||
            selectedFileStatus.some((fs) => fs.value === file.status))
      )
    );
  };

  private assignFilesToTable = (files: File[]) => {
    this.setState({
      tableData: this.buildTableData(files),
      disableRepeatDownload: false,
      downloadMessage: "",
    });
  };

  public componentDidMount() {
    // Read the route parameter from URL
    const projectIdParam = (this.props as any).match?.params?.id;

    if (!projectIdParam) {
      this.props.initAllFiles();
    }

    if (projectIdParam) {
      const projectIdNum = parseInt(projectIdParam, 10);
      this.setState({
        currentId: projectIdNum,
        hasProjectParam: true
      });

      const {
        currentId,
        currentLabel,
        currentType,
        fields,
        selectedAttachment,
        selectedFileTypes,
        selectedFileStatus,
        query,
      } = this.state;

      this.resolveStoreIds(projectIdNum)
        .then((storeIds) => {
          const queryWithStoreIds: FetchAllStoresOrFilesQueryOptions = {
            ...query,
            includeNullStore: true,
            ...(storeIds ? { storeIds } : {}),
          };

          this.setState((prevState) => ({
            query: {
              ...prevState.query,
              storeIds,
            },
          }));

          const existingFiles = this.props.files || [];
          if (existingFiles.length > 0) {
            return Promise.resolve(existingFiles);
          }

          return this.props.fetchAllFiles(projectIdNum, queryWithStoreIds).then(() => this.props.files || []);
        })
        .then((files = []) => {
          this.assignFilesToTable(files);

          this.props.createAnalyticsEvent({
            event: EventTypes.DOWNLOADS_SEARCH,
            eventType: "Action",
            projectName: currentLabel,
            projectType: currentType as ProjectType,
            searchResults: files.length,
            searchFieldsList: [
              ...Array.from(fields),
              `Attachment Type - ${selectedAttachment.value}`,
              ...selectedFileTypes.map((ft) => `File Type - ${ft.value.toLocaleUpperCase()}`),
              ...selectedFileStatus.map((ft) => `File Status - ${ft.value}`),

            ] as DownloadSearchFields[],
            searchAction: "Search",
          });
        });
    }
  }

  public componentDidUpdate(prevProps: DownloadsProps) {
    // When projects load and we have a URL param, extract names
    if (
      this.state.hasProjectParam &&
      this.props.files !== prevProps.files ||
      (this.state.hasProjectParam && !this.state.currentProjectName && this.props.projects?.length)
    ) {
      const matched = this.props.projects?.find(
        (p: any) => p.project_id === this.state.currentId
      );
      if (matched) {
        this.setState({
          currentProjectName: matched.project_name || "",
          currentChainName: String(matched.chain || ""),
          currentLabel: (matched.chain || "") + " - " + matched.project_name,
        });
      }
    }
  }

  public emailCount() {
    const fileCount = this.props.files ? this.props.files.length : 0;
    return Math.ceil(fileCount / 250);
  }

  public handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
    e.preventDefault();

    const {
      currentId,
      currentLabel,
      currentType,
      fields,
      selectedAttachment,
      selectedFileTypes,
      selectedFileStatus,
      query,
    } = this.state;
    this.resolveStoreIds(currentId)
      .then((storeIds) => {
        const queryWithStoreIds: FetchAllStoresOrFilesQueryOptions = {
          ...query,
          includeNullStore: true,
          ...(storeIds ? { storeIds } : {}),
        };

        this.setState((prevState) => ({
          query: {
            ...prevState.query,
            storeIds,
          },
        }));

        return this.props.fetchAllFiles(currentId, queryWithStoreIds);
      })
      .then(() => {
        const { files = [] } = this.props;
        this.assignFilesToTable(files);

        this.props.createAnalyticsEvent({
          event: EventTypes.DOWNLOADS_SEARCH,
          eventType: "Action",
          projectName: currentLabel,
          projectType: currentType as ProjectType,
          searchResults: files.length,
          searchFieldsList: [
            ...Array.from(fields),
            `Attachment Type - ${selectedAttachment.value}`,
            ...selectedFileTypes.map((ft) => `File Type - ${ft.value.toLocaleUpperCase()}`),
            ...selectedFileStatus.map((ft) => `File Status - ${ft.value}`),

          ] as DownloadSearchFields[],
          searchAction: "Search",
        });
      });
  };

  public handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    // reset form and inputs
    this.setState({
      query: defaultQuery,
      selectedAttachment: attachmentOptions[0],
      selectedFileTypes: [],
      selectedFileStatus: [],
      fields: new Set(),
    });

    this.props.createAnalyticsEvent({
      event: EventTypes.DOWNLOADS_SEARCH,
      eventType: "Action",
      searchAction: "Clear",
    });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      query: {
        ...this.state.query,
        [e.currentTarget.name]: e.currentTarget.value,
      },
      fields: this.state.fields.add(e.currentTarget.placeholder),
    });
  };

  public handleChangeStatus = (newValue: string) => {
    this.setState({
      query: {
        ...this.state.query,
        status: newValue,
      },
      fields: this.state.fields.add("Status"),
    });
  };

  public handleBatchDownload = (e: React.MouseEvent<HTMLFormElement>) => {
    const { currentLabel, currentType } = this.state;

    // Get permission and filter files
    const chainPermissionString = getItem(CHAIN_ROLE);
    const chainPermission = JSON.parse(chainPermissionString || "[]");
    const RestrictedRole = [
      Constants.VIEWER_NO_PSA_ACCESS,
      Constants.CONTRIBUTER_NO_PSA_ACCESS
    ];
    const restrictedUser = RestrictedRole.includes(chainPermission.role);

    // Filter files for restricted users
    const filesToDownload = restrictedUser
      ? this.state.tableData.filter(
          (file: File) => !file?.name.toLowerCase().endsWith(".psa")
        )
      : this.state.tableData;

    const fileList: Array<Partial<File>> = filesToDownload.map((file) => {
      return {
        status: file.status,
        blob_url: file.blob_url,
        filename: file.name,
      };
    });

    this.props.createAnalyticsEvent({
      event: EventTypes.DOWNLOAD_FILES,
      eventType: "Action",
      projectName: currentLabel,
      projectType: currentType as ProjectType,
      downloadFileCount: fileList.length,
    });

    // Only split on the FIRST dash, keep the rest intact
    const project = this.state.currentProjectName;
    const chain = this.state.currentChainName;;

    // const [chain, project] = currentLabel.split(/-/).map((l) => l.trim());
    this.props
      .fetchZipForFilesDownload(project, chain, fileList)
      .then(() => {
        if (this.props.hasError) {
          return;
        }

        // Trigger blob download
        // const blob = this.props.batch_download;
        // if (blob) {
        //   const url = window.URL.createObjectURL(new Blob([blob], { type: "application/zip" }));
        //   const link = document.createElement("a");
        //   link.href = url;
        //   link.setAttribute("download", `${project}.zip`);
        //   document.body.appendChild(link);
        //   link.click();
        //   link.parentNode?.removeChild(link);
        //   window.URL.revokeObjectURL(url); // free memory
        // }

        this.setState({
          ...this.state,
          downloadMessage: Constants.DOWNLOAD_FILES_SUCCESS_MESSAGE,
          disableRepeatDownload: true,
        });
      })
      .catch(() =>
        this.setState({
          ...this.state,
          downloadMessage: Constants.DOWNLOAD_FILES_ERROR_MESSAGE,
        })
      );
  };

  public handleDownloadClick = (e: React.MouseEvent<HTMLButtonElement>, file: File) => {
    const { name, maptype, status, blob_url: blobUrl, id } = file;
    const { currentLabel, currentType } = this.state;

    this.props.createAnalyticsEvent({
      event: EventTypes.DOWNLOAD_FILES,
      eventType: "Action",
      projectName: currentLabel,
      projectType: currentType as ProjectType,
      downloadFileCount: 1,
      downloadFileType: maptype as FileType,
      downloadFileName: name,
      downloadFileStatus: status as FileStatus,
    });
    // return window.open(blobUrl);
    if (!id) {
      console.error("No file URL available for download");
      return;
    }
    return window.open(`${config.API_SERVICE_URL}/api/file/${id}`);
  };

  public renderDownloadStatus() {
    if (this.props.isLoadingDownloads) {
      return <img className="spin-right" src={loadingSvg} alt="Loading Icon" />;
    } else if (this.props.hasError) {
      return (
        <span className="download-error error">
          {Constants.DOWNLOAD_FILES_DOWNLOAD_FAILED_MESSAGE}
        </span>
      );
    } else if (this.state.downloadMessage) {
      return <span className="download-message">{this.state.downloadMessage}</span>;
    }
    return;
  }

  public renderForm() {
    const { currentId } = this.state;
    const buttonDisabled = currentId < 0;
    const disabled = buttonDisabled || this.state.selectedAttachment.label === PROJ_ATTACHMENTS;
    const colorPicker: string =
      this.state.query.status && this.state.query.status !== "Store Status" ? "#4a4a4a" : "#acacac";

    return (
      <Form style={{padding: 0}} onSubmit={this.handleSubmit}>
        <Form.ButtonGroup>
          <Form.CancelButton
            onClick={this.handleClear}
            disabled={buttonDisabled && this.state.selectedFileTypes.length === 0 && this.state.selectedFileStatus.length === 0}
            data-qa="clear-button"
          >
            Clear
          </Form.CancelButton>
          <Form.ConfirmButton disabled={buttonDisabled} data-qa="search-button">
            Search
          </Form.ConfirmButton>
        </Form.ButtonGroup>
      </Form>
    );
  }

  public render() {
    const { disableRepeatDownload } = this.state;

    // Permission logic
    const chainPermissionString = getItem(CHAIN_ROLE);
    const chainPermission = JSON.parse(chainPermissionString || "[]");
    const permission = chainPermission;

    const AllowedRole = [
      Constants.INTERNAL_PROJECT_ADMIN,
      Constants.EXTERNAL_PROJECT_ADMIN
    ];
    const projectAdmins = AllowedRole.includes(
      permission.role_name ? permission.role_name : permission.role
    );

    const RestrictedRole = [
      Constants.VIEWER_NO_PSA_ACCESS,
      Constants.CONTRIBUTER_NO_PSA_ACCESS
    ];
    const restrictedUser = RestrictedRole.includes(permission.role);
    // Filter files based on user role
    let displayFiles: File[] = [];
    if (restrictedUser) {
      // Remove .psa files from files array
      displayFiles = this.state.tableData.filter(
        (file: File) => !file?.name.toLowerCase().endsWith(".psa")
      );
    } else {
      displayFiles = this.state.tableData;
    }

    return (
      <Page>
        {/* <h2>Downloads</h2> */}
        <CardList>
          <Card style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "70px", }}>
            <Card.Header style={{border: 0}}>
              <div className="downloads-header">
                <div data-qa="select-project">
                  <ProjectFilter
                    key={this.state.hasProjectParam ? "locked" : "free"}
                    onChange={({ id, name, type }: FilterOptions) => {
                      if (!this.state.hasProjectParam) {
                        const dashIndex = name.indexOf(" - ");
                        const chainName = dashIndex !== -1 ? name.substring(0, dashIndex).trim() : "";
                        const projectName = dashIndex !== -1 ? name.substring(dashIndex + 3).trim() : name;
                        this.setState({
                          currentId: id,
                          currentLabel: name,
                          currentType: type,
                          currentChainName: chainName,
                          currentProjectName: projectName,
                        });
                      }
                    }}
                    isSearchable={!this.state.hasProjectParam}
                    isDisabled={this.state.hasProjectParam}
                    value={this.state.currentId > 0 ? this.state.currentId : undefined}
                  />
                </div>
                <div data-qa="select-file-type">
                  <FileTypeFilter
                    fileTypeOptions={fileTypeOptions}
                    onChange={(selectedFileTypes: ReactSelectObject[]) => {
                      this.setState({ selectedFileTypes });
                    }}
                    value={this.state.selectedFileTypes}
                    isSearchable
                  />
                </div>
                <div data-qa="select-file-status">
                  <FileStatusFilter
                    fileStatusOptions={fileStatusOptions}
                    onChange={(selectedFileStatus: ReactSelectObject[]) => {
                      this.setState({ selectedFileStatus });
                    }}
                    value={this.state.selectedFileStatus}
                    isSearchable
                  />
                </div>
                <div data-qa="select-attachments">
                  <Dropdown
                    containerStyles={{ minWidth: 230, marginLeft: 10 }}
                    placeholder="Select Attachments"
                    options={attachmentOptions}
                    value={this.state.selectedAttachment}
                    onChange={(selectedAttachment: ReactSelectObject) => {
                      this.setState({ selectedAttachment });
                    }}
                    isSearchable
                  />
                </div>
              </div>
            </Card.Header>
            <Card.Body style={{ display: "flex", alignItems: "center" }}>{this.renderForm()}</Card.Body>
          </Card>
          <Card>
            <Card.Header className="table-header">
              Results
              <div className="download-all-button">
                {this.renderDownloadStatus()}
                {this.emailCount() > 1 ? (
                  <ButtonWithConfirmation
                    typeConfiguration={{ secondary: true }}
                    onPrimaryAction={this.handleBatchDownload}
                    disabled={
                      displayFiles.length < 1 ||
                      this.props.isLoadingDownloads ||
                      disableRepeatDownload
                    }
                    openDialogButtonContent="Download All Files"
                    primaryButtonContent="Download"
                    secondaryButtonContent="Cancel"
                    primaryContent={() => <p>Are you sure you wish to download all files?</p>}
                    secondaryContent={() => (
                      <div className="items-center">
                        <p>
                          Due to the volume of files, this email will be sent as {this.emailCount()}{" "}
                          separate emails.
                        </p>
                      </div>
                    )}
                    icon={DialogIcon.CAUTION}
                    getIconProps={{ large: true }}
                  />
                ) : (
                  <Button
                    onClick={this.handleBatchDownload}
                    secondary={true}
                    hasTooltip={disableRepeatDownload}
                    disabled={
                      displayFiles.length < 1 ||
                      this.props.isLoadingDownloads ||
                      disableRepeatDownload
                    }
                    style={styles.button("transparent", "#E5B42F", "1px solid #E5B42F")}
                  >
                    <Tooltip message="Please select a different set of files to download." />
                    Download All Files
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <Table
                data={displayFiles}
                loading={this.props.isLoadingFiles}
                emptyState="Select a project and search to view results"
                columns={[
                  {
                    field: "storeno",
                    header: "Store No.",
                    dataQa: "store-number",
                    sortMethod: (a: string | number, b: string | number) => sortStoreNumbersMixed(a, b),
                    show: showStoreColumns,
                  },
                {
                  header: "Store Status",
                  field: "store_status",
                  formatter: (cell,row) => {
                    let projectType = row.type_id === 1 ? Constants.VALIDATION : Constants.FILE_SHARING;
                    const pill = getPillProps(cell, projectType);
                    return <Pill {...pill} />;
                  },
                    wrap: true,
                    minWidth: 160,
                    show: showStoreColumns,
                    dataQa: "store-status",
                },
                  {
                    field: "address",
                    header: "Address",
                    wrap: true,
                    minWidth: 200,
                    dataQa: "address",
                    show: showStoreColumns,
                  },
                  {
                    field: "name",
                    header: "File Name",
                    wrap: true,
                    dataQa: "file-name",
                  },
                  {
                    field: "status",
                    header: "File Status",
                    formatter: (value) => {
                      const pill = getPillProps(value.toLocaleUpperCase() as PillType);

                      return <Pill {...pill} />;
                    },
                    dataQa: "file-status",
                  },
                  {
                    field: "extension",
                    header: "File Type",
                    formatter: (value) => value.toLocaleUpperCase(),
                    dataQa: "file-extension",
                  },
                  {
                    field: "user_name",
                    header: "Uploaded By",
                    wrap: true,
                    dataQa: "uploaded-by",
                  },
                  {
                    field: "updated_at",
                    header: "Upload Date",
                    wrap: true,
                    dataQa: "upload-date",
                    formatter: (value) => {
                      const date = moment(value);

                      if (!date.isValid()) {
                        return "--";
                      }

                      return date.format("M/D/YYYY h:mm A");
                    },
                  },
                  {
                    field: "id",
                    header: "",
                    sortable: false,
                    formatter: (value: string, row: File) => {
                      return (
                        <button
                          className="download-button"
                          data-qa="download-file-button"
                          onClick={(e) => this.handleDownloadClick(e, row)}
                        >
                          <Icon src={downloadSvg} />
                        </button>
                      );
                    },
                  },
                ]}
              />
            </Card.Body>
          </Card>
        </CardList>
      </Page>
    );
  }
}

const connector = connect(
  (state: State) => ({
    files: state.files.content?.data,
    stores: state.stores.content?.data,
    batch_download: state.batch_download.content,
    hasError: state.batch_download.error,
    isLoadingDownloads: state.batch_download.loading,
    isLoadingFiles: state.files.loading,
    projects: state.my_projects.content,
  }),
  {
    createAnalyticsEvent,
    initAllFiles,
    fetchAllFiles,
    fetchStoresByProjectId,
    fetchZipForFilesDownload,
  }
);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Downloads);
